import React from 'react';
import styles from './style.module.scss';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { InsightsResponse } from '@zoocasa/go-search';

export interface InvestmentStatsProps {
  /** The insights data containing market statistics */
  insightsData?: InsightsResponse;
  /** The name of the area for display */
  areaName?: string;
}

/**
 * Minimum number of listings required before displaying a property type's
 * average price. Matches ZoopraisalData.DEFAULT_AVAILABILITY_THRESHOLD.
 * With fewer listings the average is too easily skewed by outliers.
 */
const MIN_LISTINGS_THRESHOLD = 3;

/**
 * Listings at or above this count are considered a reliable sample.
 * Below this (but ≥ MIN_LISTINGS_THRESHOLD) we show a "small sample" note.
 */
const SMALL_SAMPLE_CEILING = 6;

/**
 * Displays investment-focused statistics for an area.
 * Uses existing InsightsResponse data to show key metrics,
 * price breakdowns by property type, inventory & absorption,
 * rental yield indicators, price volatility, and top neighbourhoods.
 */
const InvestmentStats = ({ insightsData, areaName }: InvestmentStatsProps) => {
  const { formatCurrency, formatNumber } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  if (!insightsData) {
    return null;
  }

  // ── Core Stats ──
  const medianPrice = insightsData.MedianPurchasePrice ?? 0;
  const medianRentPrice = insightsData.MedianRentPrice ?? 0;
  const daysOnMarket = insightsData.DaysOnMarket ?? 0;
  const avgPricePerSqft = insightsData.AvgPricePerSqft ?? 0;
  const sellListRatio = insightsData.SellListRatio ?? 0;
  const avgTotalPrice = insightsData.AvgTotalPrice ?? 0;

  // ── Price Breakdown by Property Type ──
  const avgHousePrice = insightsData.AvgHousePrice ?? 0;
  const avgTownhousePrice = insightsData.AvgTownhousePrice ?? 0;
  const avgCondoPrice = insightsData.AvgCondoPrice ?? 0;
  const avgRentalPrice = insightsData.AvgRentalPrice ?? 0;

  // ── Inventory ──
  const totalHouses = insightsData.TotalHouses ?? 0;
  const totalTownhouses = insightsData.TotalTownhouses ?? 0;
  const totalCondos = insightsData.TotalCondos ?? 0;
  const totalRentals = insightsData.TotalRentals ?? 0;
  const totalSaleInventory = totalHouses + totalTownhouses + totalCondos;
  const totalInventory = totalSaleInventory + totalRentals;

  // ── Trends ──
  const trends = insightsData.medianPriceTrends ?? [];
  const sortedTrends = [...trends].sort((a, b) => a.date.localeCompare(b.date));
  const latestMonth = sortedTrends.length > 0 ? sortedTrends[sortedTrends.length - 1] : null;
  const previousMonth = sortedTrends.length > 1 ? sortedTrends[sortedTrends.length - 2] : null;

  // Sold listings from the most recent period
  const recentSoldCount = latestMonth?.docCount ?? 0;

  // Year-over-year price change
  let yoyPriceChange: number | null = null;
  if (sortedTrends.length >= 13) {
    const currentPrice = sortedTrends[sortedTrends.length - 1]?.medianPrice;
    const yearAgoPrice = sortedTrends[sortedTrends.length - 13]?.medianPrice;
    if (currentPrice && yearAgoPrice && yearAgoPrice > 0) {
      yoyPriceChange = ((currentPrice - yearAgoPrice) / yearAgoPrice) * 100;
    }
  }

  // Month-over-month price change
  let momPriceChange: number | null = null;
  if (latestMonth && previousMonth && previousMonth.medianPrice > 0) {
    momPriceChange = ((latestMonth.medianPrice - previousMonth.medianPrice) / previousMonth.medianPrice) * 100;
  }

  // ── Rental Yield / Cap Rate ──
  let grossRentalYield: number | null = null;
  if (medianRentPrice > 0 && medianPrice > 0) {
    grossRentalYield = ((medianRentPrice * 12) / medianPrice) * 100;
  }

  let capRateEstimate: number | null = null;
  if (avgRentalPrice > 0 && avgTotalPrice > 0) {
    capRateEstimate = ((avgRentalPrice * 12) / avgTotalPrice) * 100;
  }

  // Rent multiplier: years of rent to pay off purchase
  let rentMultiplier: number | null = null;
  if (avgRentalPrice > 0 && avgCondoPrice > 0) {
    rentMultiplier = avgCondoPrice / (avgRentalPrice * 12);
  }

  // ── Absorption Rate: months of inventory at current sales pace ──
  let absorptionRate: number | null = null;
  if (recentSoldCount > 0 && totalSaleInventory > 0) {
    absorptionRate = totalSaleInventory / recentSoldCount;
  }

  // ── Rental Inventory Share ──
  let rentalInventoryShare: number | null = null;
  if (totalInventory > 0) {
    rentalInventoryShare = (totalRentals / totalInventory) * 100;
  }

  // ── Price Volatility (std dev of monthly median prices) ──
  let priceVolatility: number | null = null;
  if (sortedTrends.length >= 3) {
    const prices = sortedTrends.map(trend => trend.medianPrice).filter(p => p > 0);
    if (prices.length >= 3) {
      const mean = prices.reduce((a, b) => a + b, 0) / prices.length;
      const variance = prices.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) / prices.length;
      const stdDev = Math.sqrt(variance);
      // Express as coefficient of variation (percentage of mean)
      priceVolatility = (stdDev / mean) * 100;
    }
  }

  const hasData = medianPrice > 0 || recentSoldCount > 0 || avgTotalPrice > 0;

  if (!hasData) {
    return null;
  }

  const getPriceChangeClass = (change: number | null) => {
    if (change === null) return styles.neutral;
    return change >= 0 ? styles.positive : styles.negative;
  };

  const formatPriceChange = (change: number | null) => {
    if (change === null) return t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_NOTAVAILABLE, { defaultValue: 'N/A' });
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  // Only consider a property type's price reliable if it has enough listings
  const houseReliable = avgHousePrice > 0 && totalHouses >= MIN_LISTINGS_THRESHOLD;
  const townhouseReliable = avgTownhousePrice > 0 && totalTownhouses >= MIN_LISTINGS_THRESHOLD;
  const condoReliable = avgCondoPrice > 0 && totalCondos >= MIN_LISTINGS_THRESHOLD;
  const hasPriceBreakdown = houseReliable || townhouseReliable || condoReliable;
  const hasInventory = totalSaleInventory > 0 || totalRentals > 0;
  const hasRentalMetrics = grossRentalYield !== null || capRateEstimate !== null || rentMultiplier !== null || avgRentalPrice > 0;
  const hasTrends = yoyPriceChange !== null || momPriceChange !== null || priceVolatility !== null;

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {areaName ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_HEADINGWITHAREA, { areaName, defaultValue: 'Investment Overview — {{areaName}}' }) : t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_HEADING, { defaultValue: 'Investment Overview' })}
      </h2>
      <p className={styles.description}>
        {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_DESCRIPTION, { defaultValue: 'Key metrics for evaluating real estate investment potential in this area.' })}
      </p>

      {/* ── Core Stats Grid ── */}
      <div className={styles.statsGrid}>
        {recentSoldCount > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H2v7l6.29 6.29a1 1 0 0 0 1.42 0l5.58-5.58a1 1 0 0 0 0-1.42L9 5z" />
                <circle cx="6" cy="9" r="1" fill="currentColor" />
              </svg>
            </div>
            <div className={styles.statValue}>{formatNumber(recentSoldCount)}</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SOLDLISTINGSLABEL, { defaultValue: 'Sold Listings' })}</div>
            <div className={styles.statHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SOLDLISTINGSHINT, { defaultValue: 'Most recent reporting period' })}</div>
          </div>
        )}

        {medianPrice > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </div>
            <div className={styles.statValue}>{formatCurrency(medianPrice)}</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_MEDIANSOLDPRICELABEL, { defaultValue: 'Median Sold Price' })}</div>
            <div className={styles.statHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}</div>
          </div>
        )}

        {avgTotalPrice > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                <line x1="2" y1="7" x2="22" y2="7" />
              </svg>
            </div>
            <div className={styles.statValue}>{formatCurrency(avgTotalPrice)}</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVGSOLDPRICELABEL, { defaultValue: 'Avg. Sold Price' })}</div>
            <div className={styles.statHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVGSOLDPRICEHINT, { defaultValue: 'Across all property types' })}</div>
          </div>
        )}

        {daysOnMarket > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className={styles.statValue}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVGDAYSONMARKETVALUE, { days: String(daysOnMarket), defaultValue: '{{days}} days' })}</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVGDAYSONMARKETLABEL, { defaultValue: 'Avg. Days on Market' })}</div>
            <div className={styles.statHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_TIMETOCLOSEHINT, { defaultValue: 'Time to close' })}</div>
          </div>
        )}

        {avgPricePerSqft > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="9" y1="21" x2="9" y2="9" />
              </svg>
            </div>
            <div className={styles.statValue}>{formatCurrency(avgPricePerSqft)}</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_PRICEPERSQFTLABEL, { defaultValue: 'Price per Sq.Ft' })}</div>
            <div className={styles.statHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVERAGEACROSSAREAHINT, { defaultValue: 'Average across area' })}</div>
          </div>
        )}

        {sellListRatio > 0 && (
          <div className={styles.statCard}>
            <div className={styles.statIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                <polyline points="17 6 23 6 23 12" />
              </svg>
            </div>
            <div className={styles.statValue}>{(sellListRatio * 100).toFixed(1)}%</div>
            <div className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SALETOLISTRATIOLABEL, { defaultValue: 'Sale-to-List Ratio' })}</div>
            <div className={styles.statHint}>
              {sellListRatio >= 1 ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SELLERSMARKETHINT, { defaultValue: 'Sellers\' market' }) : t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_ROOMFORNEGOTIATIONHINT, { defaultValue: 'Room for negotiation' })}
            </div>
          </div>
        )}
      </div>

      {/* ── Price Breakdown by Property Type ── */}
      {hasPriceBreakdown && (
        <div className={styles.breakdownSection}>
          <h3 className={styles.sectionHeading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
            {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_PRICEBYPROPERTYTYPEHEADING, { defaultValue: 'Price by Property Type' })}
          </h3>
          <div className={styles.breakdownGrid}>
            {houseReliable && (
              <div className={styles.breakdownCard}>
                <div className={styles.breakdownLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_HOUSESLABEL, { defaultValue: 'Houses' })}</div>
                <div className={styles.breakdownValue}>{formatCurrency(avgHousePrice)}</div>
                <div className={styles.breakdownCount}>
                  {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_LISTINGSCOUNT, { count: totalHouses, defaultValue: '{{count}} listings' })}
                  {totalHouses < SMALL_SAMPLE_CEILING && (
                    <span className={styles.smallSample}> · {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SMALLSAMPLE, { defaultValue: 'Small sample' })}</span>
                  )}
                </div>
              </div>
            )}
            {townhouseReliable && (
              <div className={styles.breakdownCard}>
                <div className={styles.breakdownLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_TOWNHOUSESLABEL, { defaultValue: 'Townhouses' })}</div>
                <div className={styles.breakdownValue}>{formatCurrency(avgTownhousePrice)}</div>
                <div className={styles.breakdownCount}>
                  {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_LISTINGSCOUNT, { count: totalTownhouses, defaultValue: '{{count}} listings' })}
                  {totalTownhouses < SMALL_SAMPLE_CEILING && (
                    <span className={styles.smallSample}> · {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SMALLSAMPLE, { defaultValue: 'Small sample' })}</span>
                  )}
                </div>
              </div>
            )}
            {condoReliable && (
              <div className={styles.breakdownCard}>
                <div className={styles.breakdownLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_CONDOSLABEL, { defaultValue: 'Condos' })}</div>
                <div className={styles.breakdownValue}>{formatCurrency(avgCondoPrice)}</div>
                <div className={styles.breakdownCount}>
                  {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_LISTINGSCOUNT, { count: totalCondos, defaultValue: '{{count}} listings' })}
                  {totalCondos < SMALL_SAMPLE_CEILING && (
                    <span className={styles.smallSample}> · {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SMALLSAMPLE, { defaultValue: 'Small sample' })}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Inventory & Absorption ── */}
      {hasInventory && (
        <div className={styles.breakdownSection}>
          <h3 className={styles.sectionHeading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_INVENTORYABSORPTIONHEADING, { defaultValue: 'Inventory & Absorption' })}
          </h3>
          <div className={styles.inventoryRow}>
            {totalHouses > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.inventoryCount}>{formatNumber(totalHouses)}</span>
                <span className={styles.inventoryType}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_HOUSESLABEL, { defaultValue: 'Houses' })}</span>
              </div>
            )}
            {totalTownhouses > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.inventoryCount}>{formatNumber(totalTownhouses)}</span>
                <span className={styles.inventoryType}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_TOWNHOUSESLABEL, { defaultValue: 'Townhouses' })}</span>
              </div>
            )}
            {totalCondos > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.inventoryCount}>{formatNumber(totalCondos)}</span>
                <span className={styles.inventoryType}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_CONDOSLABEL, { defaultValue: 'Condos' })}</span>
              </div>
            )}
            {totalRentals > 0 && (
              <div className={styles.inventoryItem}>
                <span className={styles.inventoryCount}>{formatNumber(totalRentals)}</span>
                <span className={styles.inventoryType}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_RENTALSLABEL, { defaultValue: 'Rentals' })}</span>
              </div>
            )}
          </div>
          {absorptionRate !== null && (
            <div className={styles.absorptionBar}>
              <div className={styles.absorptionLabel}>
                <span>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_ABSORPTIONRATELABEL, { defaultValue: 'Absorption Rate' })}</span>
                <span className={styles.absorptionValue}>
                  {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_MONTHSOFINVENTORY, { months: absorptionRate.toFixed(1), defaultValue: '{{months}} months of inventory' })}
                </span>
              </div>
              <div className={styles.absorptionTrack}>
                <div
                  className={styles.absorptionFill}
                  style={{ width: `${Math.min((absorptionRate / 12) * 100, 100)}%` }}
                />
              </div>
              <div className={styles.absorptionHint}>
                {absorptionRate < 4
                  ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_SELLERSMARKETDESC, { defaultValue: 'Seller\'s market — low supply, high demand' })
                  : absorptionRate <= 6
                    ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_BALANCEDMARKET, { defaultValue: 'Balanced market' })
                    : t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_BUYERSMARKETDESC, { defaultValue: 'Buyer\'s market — more room for negotiation' })}
              </div>
            </div>
          )}
          {rentalInventoryShare !== null && rentalInventoryShare > 0 && (
            <div className={styles.trendRow}>
              <span className={styles.trendLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_RENTALINVENTORYSHARELABEL, { defaultValue: 'Rental Inventory Share' })}</span>
              <span className={styles.trendValue}>{rentalInventoryShare.toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}

      {/* ── Rental Yield & ROI Indicators ── */}
      {hasRentalMetrics && (
        <div className={styles.breakdownSection}>
          <h3 className={styles.sectionHeading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            {t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_RENTALYIELDROIHEADING, { defaultValue: 'Rental Yield & ROI' })}
          </h3>
          <div className={styles.roiGrid}>
            {avgRentalPrice > 0 && (
              <div className={styles.roiCard}>
                <div className={styles.roiValue}>{formatCurrency(avgRentalPrice)}</div>
                <div className={styles.roiLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_AVGMONTHLYRENTLABEL, { defaultValue: 'Avg. Monthly Rent' })}</div>
              </div>
            )}
            {grossRentalYield !== null && (
              <div className={styles.roiCard}>
                <div className={`${styles.roiValue} ${grossRentalYield >= 4 ? styles.positive : styles.neutral}`}>
                  {grossRentalYield.toFixed(1)}%
                </div>
                <div className={styles.roiLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_GROSSRENTALYIELDLABEL, { defaultValue: 'Gross Rental Yield' })}</div>
                <div className={styles.roiHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_GROSSRENTALYIELDHINT, { defaultValue: 'Annual rent ÷ median price' })}</div>
              </div>
            )}
            {capRateEstimate !== null && (
              <div className={styles.roiCard}>
                <div className={`${styles.roiValue} ${capRateEstimate >= 4 ? styles.positive : styles.neutral}`}>
                  {capRateEstimate.toFixed(1)}%
                </div>
                <div className={styles.roiLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_ESTCAPRATELABEL, { defaultValue: 'Est. Cap Rate' })}</div>
                <div className={styles.roiHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_ESTCAPRATEHINT, { defaultValue: 'Annual rent ÷ avg. price' })}</div>
              </div>
            )}
            {rentMultiplier !== null && (
              <div className={styles.roiCard}>
                <div className={styles.roiValue}>{rentMultiplier.toFixed(1)}x</div>
                <div className={styles.roiLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_RENTMULTIPLIERLABEL, { defaultValue: 'Rent Multiplier' })}</div>
                <div className={styles.roiHint}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_RENTMULTIPLIERHINT, { defaultValue: 'Years of rent to cover purchase (condos)' })}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Market Trends & Volatility ── */}
      {hasTrends && (
        <div className={styles.trendsSection}>
          <h3 className={styles.trendsHeading}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_MARKETTRENDSHEADING, { defaultValue: 'Market Trends' })}</h3>

          {yoyPriceChange !== null && (
            <div className={styles.trendRow}>
              <span className={styles.trendLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_YOYPRICECHANGELABEL, { defaultValue: 'Year-over-Year Price Change' })}</span>
              <span className={`${styles.trendValue} ${getPriceChangeClass(yoyPriceChange)}`}>
                {formatPriceChange(yoyPriceChange)}
              </span>
            </div>
          )}

          {momPriceChange !== null && (
            <div className={styles.trendRow}>
              <span className={styles.trendLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_MOMPRICECHANGELABEL, { defaultValue: 'Month-over-Month Price Change' })}</span>
              <span className={`${styles.trendValue} ${getPriceChangeClass(momPriceChange)}`}>
                {formatPriceChange(momPriceChange)}
              </span>
            </div>
          )}

          {priceVolatility !== null && (
            <div className={styles.trendRow}>
              <span className={styles.trendLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_PRICEVOLATILITYLABEL, { defaultValue: 'Price Volatility' })}</span>
              <span className={`${styles.trendValue} ${priceVolatility > 10 ? styles.negative : priceVolatility > 5 ? styles.neutral : styles.positive}`}>
                {priceVolatility.toFixed(1)}%
                <span className={styles.volatilityHint}>
                  {' — '}
                  {priceVolatility > 10
                    ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_VOLATILITYHIGH, { defaultValue: 'High' })
                    : priceVolatility > 5
                      ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_VOLATILITYMODERATE, { defaultValue: 'Moderate' })
                      : t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_VOLATILITYLOW, { defaultValue: 'Low' })}
                </span>
              </span>
            </div>
          )}

          {sellListRatio > 0 && (
            <div className={styles.trendRow}>
              <span className={styles.trendLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_MARKETCOMPETITIVENESSLABEL, { defaultValue: 'Market Competitiveness' })}</span>
              <span className={styles.trendValue}>
                {sellListRatio >= 1.02
                  ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_HOTMARKET, { defaultValue: '🔥 Hot — homes selling above asking' })
                  : sellListRatio >= 0.98
                    ? t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_COMPETITIVEMARKET, { defaultValue: '⚡ Competitive — close to asking price' })
                    : t(LOCALE_COMMON_PATHS.CITYINFO_INVESTMENTSTATS_COOLERMARKET, { defaultValue: '📉 Cooler — room for negotiation' })}
              </span>
            </div>
          )}
        </div>
      )}

    </section>
  );
};

export default InvestmentStats;
